/**
 * One questionnaire, being worked.
 *
 * Drafting runs in batches so the progress is real rather than a spinner that
 * might mean anything, and so a failure loses ten questions instead of two
 * hundred. Refusals are shown in full — the question, the reason, and what to go
 * and write — because the gap list is the half of this product that compounds.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  api,
  errorMessage,
  type Evidence,
  type PreviousAnswer,
  type Question,
} from '../api';
import { DRAFT_BATCH_LIMIT } from '../../core/limits';
import { buildAnswerWorkbook, downloadBlob, fillWorkbook, readWorkbook } from '../sheet';
import { link } from '../router';
import {
  Alert,
  Button,
  Dialog,
  Field,
  Input,
  PageHeader,
  Spinner,
  Textarea,
  formatDate,
} from '../ui';
import {
  ArrowLeftIcon,
  CheckIcon,
  ClockIcon,
  DownloadIcon,
  Icon,
  IconHover,
  KeyIcon,
  PencilIcon,
  SparklesIcon,
  TriangleAlertIcon,
  UploadIcon,
} from '../icons';

const KEY_STORAGE = 'vouchkeep.apiKey';

const REFUSAL_REASON: Record<string, string> = {
  no_citations: 'Nothing in the bank supports an answer to this.',
  unknown_evidence: 'The draft cited evidence that does not exist.',
  stale_evidence: 'The only supporting evidence is past its review date.',
  empty_text: 'The draft cited evidence but said nothing.',
};

function StatusChip({ question }: { question: Question }) {
  if (question.status === 'answered') {
    return (
      <span className="chip chip-accent">
        <Icon icon={CheckIcon} size={12} isAnimated={false} />
        Answered
      </span>
    );
  }
  if (question.status === 'unsupported') {
    return (
      <span className="chip chip-refusal">
        <Icon icon={TriangleAlertIcon} size={12} isAnimated={false} />
        No supporting evidence
      </span>
    );
  }
  return <span className="chip chip-neutral">Not drafted</span>;
}

/** The key box. Says plainly where the key goes, because this buyer will ask. */
function ApiKeyBar({ value, onChange }: { value: string; onChange: (next: string) => void }) {
  return (
    <div className="card mb-5 p-4">
      <div className="flex flex-wrap items-end gap-3">
        <div className="min-w-56 flex-1">
          <Field
            label="Your Anthropic API key"
            hint="Used for this request and never stored. It reaches Anthropic through this app and is written to no database and no log."
          >
            <Input
              type="password"
              value={value}
              onChange={(e) => onChange(e.target.value)}
              placeholder="sk-ant-..."
              autoComplete="off"
              spellCheck={false}
            />
          </Field>
        </div>
        <span className="mb-7 flex items-center gap-1.5 text-[12.5px] text-faint">
          <Icon icon={KeyIcon} size={13} isAnimated={false} />
          Kept in this tab only
        </span>
      </div>
    </div>
  );
}

function AnswerEditor({
  question,
  evidence,
  onClose,
  onSaved,
  questionnaireId,
}: {
  question: Question;
  evidence: Evidence[];
  questionnaireId: string;
  onClose: () => void;
  onSaved: (updated: Question) => void;
}) {
  const [text, setText] = useState(question.answerText);
  const [citations, setCitations] = useState<string[]>(question.citations);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      const { answer } = await api.answerQuestion(questionnaireId, question.id, {
        text,
        citations,
      });
      onSaved({
        ...question,
        status: answer.status,
        answerText: answer.text,
        citations: answer.citations,
        reason: answer.reason ?? null,
      });
      if (answer.status === 'unsupported') {
        setError(
          REFUSAL_REASON[answer.reason ?? ''] ??
            'That answer was refused because it is not supported by fresh evidence.',
        );
        setBusy(false);
        return;
      }
      onClose();
    } catch (err) {
      setError(errorMessage(err));
      setBusy(false);
    }
  };

  return (
    <Dialog
      title="Write this answer"
      description="Same rule as the model: it ships with evidence or it does not ship."
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" icon={CheckIcon} busy={busy} onClick={save}>
            Save
          </Button>
        </>
      }
    >
      <p className="mb-4 rounded-lg bg-sunken px-3 py-2.5 text-[13.5px]">{question.text}</p>

      {error ? (
        <div className="mb-4">
          <Alert>{error}</Alert>
        </div>
      ) : null}

      <div className="space-y-4">
        <Field label="Answer">
          <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={5} />
        </Field>

        <div>
          <p className="label">Evidence this rests on</p>
          <div className="max-h-56 space-y-1.5 overflow-y-auto rounded-lg border border-line p-2">
            {evidence.length === 0 ? (
              <p className="px-2 py-4 text-center text-[13px] text-faint">
                The bank is empty. Nothing can be answered until there is something to cite.
              </p>
            ) : (
              evidence.map((card) => (
                <label
                  key={card.id}
                  className={`flex cursor-pointer items-start gap-2.5 rounded-md px-2 py-1.5 text-[13px] ${
                    card.stale ? 'opacity-55' : 'hover:bg-sunken'
                  }`}
                >
                  <input
                    type="checkbox"
                    className="mt-1 accent-[var(--vk-accent)]"
                    checked={citations.includes(card.id)}
                    disabled={card.stale}
                    onChange={(e) =>
                      setCitations(
                        e.target.checked
                          ? [...citations, card.id]
                          : citations.filter((c) => c !== card.id),
                      )
                    }
                  />
                  <span className="min-w-0">
                    <span className="font-medium">{card.title}</span>
                    <span className="block truncate text-faint">{card.source}</span>
                    {card.stale ? (
                      <span className="chip chip-refusal mt-1">Stale — review it first</span>
                    ) : null}
                  </span>
                </label>
              ))
            )}
          </div>
        </div>
      </div>
    </Dialog>
  );
}

export function WorkspaceView({ id }: { id: string }) {
  const [customer, setCustomer] = useState('');
  const [filename, setFilename] = useState('');
  const [questions, setQuestions] = useState<Question[] | null>(null);
  const [evidence, setEvidence] = useState<Evidence[]>([]);
  const [history, setHistory] = useState<Record<string, PreviousAnswer>>({});
  const [apiKey, setApiKey] = useState('');
  const [editing, setEditing] = useState<Question | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const [filter, setFilter] = useState<'all' | 'unsupported' | 'unanswered'>('all');
  const fillInput = useRef<HTMLInputElement>(null);

  useEffect(() => {
    try {
      const stored = sessionStorage.getItem(KEY_STORAGE);
      if (stored) setApiKey(stored);
    } catch {
      // No session storage is a nuisance, not a failure.
    }
  }, []);

  const rememberKey = (next: string) => {
    setApiKey(next);
    try {
      // sessionStorage, not localStorage: closing the tab ends it, and a key on
      // disk is a key in the next backup.
      if (next) sessionStorage.setItem(KEY_STORAGE, next);
      else sessionStorage.removeItem(KEY_STORAGE);
    } catch {
      // ignored
    }
  };

  const load = useCallback(async () => {
    try {
      const data = await api.getQuestionnaire(id);
      setCustomer(data.questionnaire.customer);
      setFilename(data.questionnaire.filename);
      setQuestions(data.questions);
      setEvidence(data.evidence);
      setHistory(data.history ?? {});
    } catch (err) {
      setError(errorMessage(err));
      setQuestions([]);
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  const titles = useMemo(
    () => new Map(evidence.map((card) => [card.id, card.title])),
    [evidence],
  );

  const counts = useMemo(() => {
    const list = questions ?? [];
    return {
      total: list.length,
      answered: list.filter((q) => q.status === 'answered').length,
      unsupported: list.filter((q) => q.status === 'unsupported').length,
      pending: list.filter((q) => q.status === 'unanswered').length,
    };
  }, [questions]);

  const visible = useMemo(() => {
    const list = questions ?? [];
    if (filter === 'unsupported') return list.filter((q) => q.status === 'unsupported');
    if (filter === 'unanswered') return list.filter((q) => q.status === 'unanswered');
    return list;
  }, [questions, filter]);

  const draft = async (targets: Question[]) => {
    if (!apiKey.trim()) {
      setError('Add your Anthropic API key first.');
      return;
    }
    if (targets.length === 0) return;

    setError(null);
    setProgress({ done: 0, total: targets.length });

    for (let i = 0; i < targets.length; i += DRAFT_BATCH_LIMIT) {
      const batch = targets.slice(i, i + DRAFT_BATCH_LIMIT);
      try {
        const { answers } = await api.draft(id, {
          apiKey: apiKey.trim(),
          questionIds: batch.map((q) => q.id),
        });

        setQuestions((current) =>
          (current ?? []).map((q) => {
            const answer = answers.find((a) => a.questionId === q.id);
            if (!answer) return q;
            return {
              ...q,
              status: answer.status,
              answerText: answer.text,
              citations: answer.citations,
              reason: answer.reason ?? null,
            };
          }),
        );
      } catch (err) {
        setError(errorMessage(err));
        setProgress(null);
        return;
      }
      setProgress({ done: Math.min(i + DRAFT_BATCH_LIMIT, targets.length), total: targets.length });
    }

    setProgress(null);
  };

  const exportStandalone = async () => {
    const rows = (questions ?? []).map((q) => ({
      question: q.text,
      status: q.status,
      answerText: q.answerText,
      citations: q.citations,
    }));
    downloadBlob(
      await buildAnswerWorkbook(rows, titles),
      `${customer.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}-answers.xlsx`,
    );
  };

  const fillOriginal = async (file: File | undefined) => {
    if (!file || !questions) return;
    setError(null);
    try {
      const book = await readWorkbook(file);
      // One sheet is the common case; with several, the first that has rows deep
      // enough to hold the highest row index we recorded is the right one.
      const deepest = Math.max(...questions.map((q) => q.rowIndex), 0);
      const sheet =
        book.sheets.find((s) => s.rows.length > deepest) ?? book.sheets[0];
      if (!sheet) {
        setError('That file has no readable sheets.');
        return;
      }

      const blob = await fillWorkbook(
        file,
        sheet.name,
        questions.map((q) => ({
          rowIndex: q.rowIndex,
          status: q.status,
          answerText: q.answerText,
          citations: q.citations,
        })),
        titles,
      );
      downloadBlob(blob, file.name.replace(/(\.[^.]+)$/, '-answered$1'));
    } catch {
      setError('Could not write into that file. Use “Download answers” instead.');
    }
  };

  if (questions === null) {
    return (
      <div className="flex justify-center py-20">
        <Spinner size={20} />
      </div>
    );
  }

  const pending = questions.filter((q) => q.status === 'unanswered');

  return (
    <>
      <a
        {...link('/questionnaires')}
        className="mb-5 inline-flex items-center gap-1.5 text-[13px] text-muted hover:text-ink"
      >
        <Icon icon={ArrowLeftIcon} size={14} isAnimated={false} />
        All questionnaires
      </a>

      <PageHeader
        title={customer}
        description={`${filename} · ${counts.total} questions`}
        actions={
          <>
            <Button
              icon={UploadIcon}
              onClick={() => fillInput.current?.click()}
              disabled={counts.answered + counts.unsupported === 0}
            >
              Fill the original file
            </Button>
            <input
              ref={fillInput}
              type="file"
              accept=".xlsx,.xls,.csv"
              className="hidden"
              onChange={(e) => void fillOriginal(e.target.files?.[0])}
            />
            <Button icon={DownloadIcon} onClick={() => void exportStandalone()}>
              Download answers
            </Button>
            <Button
              variant="primary"
              icon={SparklesIcon}
              busy={progress !== null}
              disabled={pending.length === 0}
              onClick={() => draft(pending)}
            >
              {progress
                ? `Drafting ${progress.done}/${progress.total}`
                : `Draft ${pending.length} unanswered`}
            </Button>
          </>
        }
      />

      <ApiKeyBar value={apiKey} onChange={rememberKey} />

      {error ? (
        <div className="mb-4">
          <Alert>{error}</Alert>
        </div>
      ) : null}

      {evidence.length === 0 ? (
        <div className="mb-4">
          <Alert tone="warn">
            Your evidence bank is empty, so every answer will come back refused. Write a few cards
            first — that is not a limitation, it is the product.
          </Alert>
        </div>
      ) : null}

      <div className="mb-4 flex flex-wrap items-center gap-1.5">
        {(
          [
            ['all', `All ${counts.total}`],
            ['unsupported', `Unsupported ${counts.unsupported}`],
            ['unanswered', `Not drafted ${counts.pending}`],
          ] as const
        ).map(([value, label]) => (
          <button
            key={value}
            onClick={() => setFilter(value)}
            className={`chip ${filter === value ? 'chip-accent' : 'chip-neutral'}`}
          >
            {label}
          </button>
        ))}
        <span className="ml-auto text-[12.5px] text-faint">
          {counts.answered} of {counts.total} answered
        </span>
      </div>

      <div className="space-y-2.5">
        {visible.map((question) => (
          <IconHover key={question.id} className="card p-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <p className="min-w-0 flex-1 text-[14px] leading-relaxed">{question.text}</p>
              <StatusChip question={question} />
            </div>

            {question.status === 'answered' ? (
              <div className="mt-3 border-t border-line pt-3">
                <p className="prose-body text-[13.5px] text-muted">{question.answerText}</p>
                <div className="mt-2.5 flex flex-wrap items-center gap-1.5">
                  {question.citations.map((cite) => (
                    <span key={cite} className="chip chip-accent">
                      <Icon icon={CheckIcon} size={11} isAnimated={false} />
                      {titles.get(cite) ?? cite}
                    </span>
                  ))}
                </div>
              </div>
            ) : question.status === 'unsupported' ? (
              <div className="mt-3 border-t border-line pt-3">
                <p className="text-[13.5px] text-refusal">
                  {REFUSAL_REASON[question.reason ?? ''] ??
                    'Nothing in the bank supports an answer to this.'}
                </p>
                <p className="mt-1 text-[12.5px] text-faint">
                  Write the evidence and draft again, or answer it by hand with a citation.
                </p>
              </div>
            ) : null}

            {history[question.id] ? (
              <details className="mt-3 border-t border-line pt-3">
                <summary className="cursor-pointer list-none text-[12.5px] text-muted hover:text-ink">
                  <span className="chip chip-neutral">
                    <Icon icon={ClockIcon} size={11} isAnimated={false} />
                    You answered this for {history[question.id].customer} on{' '}
                    {formatDate(history[question.id].at)}
                  </span>
                </summary>
                <p className="prose-body mt-2.5 rounded-lg bg-sunken p-3 text-[13px] text-muted">
                  {history[question.id].text}
                </p>
                <p className="mt-1.5 text-[12px] text-faint">
                  Shown so two rounds of the same review do not contradict each other. It is not
                  copied in — it still has to be answered from evidence.
                </p>
              </details>
            ) : null}

            <div className="mt-3 flex flex-wrap items-center gap-1">
              <Button
                small
                variant="ghost"
                icon={SparklesIcon}
                disabled={progress !== null}
                onClick={() => draft([question])}
              >
                Draft
              </Button>
              <Button
                small
                variant="ghost"
                icon={PencilIcon}
                onClick={() => setEditing(question)}
              >
                Write by hand
              </Button>
            </div>
          </IconHover>
        ))}

        {visible.length === 0 ? (
          <p className="py-12 text-center text-[13.5px] text-faint">
            {filter === 'unsupported'
              ? 'Nothing was refused. Every drafted answer has evidence behind it.'
              : 'Nothing here.'}
          </p>
        ) : null}
      </div>

      {editing ? (
        <AnswerEditor
          question={editing}
          evidence={evidence}
          questionnaireId={id}
          onClose={() => setEditing(null)}
          onSaved={(updated) => {
            setQuestions((current) =>
              (current ?? []).map((q) => (q.id === updated.id ? updated : q)),
            );
            setEditing((current) => (current && current.id === updated.id ? updated : current));
          }}
        />
      ) : null}
    </>
  );
}
